import { uiText, useUiLanguage } from '../../../utils/uiTranslation';
import React, { useEffect, useMemo, useState } from 'react';
import { DATASET_SHAPES } from '../datasets/datasetTypes';

const toneClassForDelta = (value, inverse = false) => {
  if (typeof value !== 'number' || Number.isNaN(value) || value === 0) return 'warning';
  const positive = inverse ? value < 0 : value > 0;
  return positive ? 'success' : 'danger';
};

const formatMetricValue = (value, unit) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return value;
  if (unit === '%') return `${numeric.toFixed(1)}%`;
  if (unit === 'days' || unit === 'أيام') return `${numeric.toFixed(1)} ${unit}`;
  if (unit) return `${numeric.toLocaleString()} ${unit}`;
  return numeric.toLocaleString();
};

const buildSparkPath = (values = [], width = 72, height = 26) => {
  if (!values.length) return '';
  const max = Math.max(...values);
  const min = Math.min(...values);
  const range = max - min || 1;

  return values
    .map((value, index) => {
      const x = (index / Math.max(values.length - 1, 1)) * width;
      const y = height - (((value - min) / range) * (height - 2)) - 1;
      return `${index === 0 ? 'M' : 'L'} ${x.toFixed(1)} ${y.toFixed(1)}`;
    })
    .join(' ');
};

const RecruiterCardsVisual = ({ model }) => {
  useUiLanguage();
  const rootDataset = model.dataset;
  const [selectedViewId, setSelectedViewId] = useState(rootDataset?.selectedViewId || rootDataset?.selectors?.[0]?.id || '');

  useEffect(() => {
    const nextDefault = rootDataset?.selectedViewId || rootDataset?.selectors?.[0]?.id || '';
    if (!nextDefault) return;
    setSelectedViewId((currentId) => {
      if (!rootDataset?.views?.[currentId] || currentId !== nextDefault) {
        return nextDefault;
      }
      return currentId;
    });
  }, [rootDataset]);

  const dataset = rootDataset?.views?.[selectedViewId] || rootDataset;
  const categories = dataset?.shape === DATASET_SHAPES.CATEGORICAL_COMPARISON ? dataset.categories || [] : [];
  const visibleCategories = useMemo(() => {
    if (model.size === 'compact') return categories.slice(0, 1);
    if (model.size === 'medium') return categories.slice(0, 3);
    return categories;
  }, [categories, model.size]);

  if (!visibleCategories.length) return null;

  return (
    <div className={`analytics-visual analytics-visual-recruiter-cards ${model.size || 'large'}`}>
      <div className={`analytics-recruiter-card-grid ${model.size || 'large'}`}>
        {visibleCategories.map((category, index) => {
          const profile = category.profile || {};
          const primaryStats = profile.primaryStats || [];
          const footerStats = profile.footerStats || [];
          const sparkPath = buildSparkPath(profile.sparkline || []);
          const deltaTone = toneClassForDelta(profile.scoreDelta);
          const isClickable = Boolean(model.options?.onSelectCategory);

          return (
            <article
              key={category.id}
              className={`analytics-recruiter-card${isClickable ? ' clickable' : ''}`}
              onClick={isClickable ? () => model.options.onSelectCategory(category.id) : undefined}
              onKeyDown={isClickable ? (event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  model.options.onSelectCategory(category.id);
                }
              } : undefined}
              role={isClickable ? 'button' : undefined}
              tabIndex={isClickable ? 0 : undefined}
            >
              <div className="analytics-recruiter-card-head">
                <div className="analytics-recruiter-card-id">
                  <div className={`analytics-recruiter-card-avatar ${profile.avatarTone || category.tone || 'team-blue'}`}>
                    {profile.initials}
                  </div>
                  <div className="analytics-recruiter-card-titles">
                    <h4>{uiText(category.label)}</h4>
                    <p>{profile.roleTitle}</p>
                  </div>
                </div>
                <div className="analytics-recruiter-card-score">
                  <span className="analytics-recruiter-card-rank">{profile.rankLabel || `#${index + 1}`}</span>
                  <strong>{Math.round(profile.score || category.metrics?.compositeScore || 0)}</strong>
                  <span className={`analytics-recruiter-card-delta ${deltaTone}`}>
                    {typeof profile.scoreDelta === 'number'
                      ? `${profile.scoreDelta > 0 ? '+' : ''}${profile.scoreDelta.toFixed(1)} ${profile.deltaLabel || ''}`.trim()
                      : profile.deltaLabel}
                  </span>
                </div>
              </div>

              <div className="analytics-recruiter-card-top-metrics">
                {primaryStats.map((stat) => (
                  <div key={stat.id} className="analytics-recruiter-card-top-metric">
                    <span className="analytics-recruiter-card-top-label">{uiText(stat.label)}</span>
                    <strong className="analytics-recruiter-card-top-value">{formatMetricValue(stat.value, stat.unit)}</strong>
                  </div>
                ))}
              </div>

              <div className="analytics-recruiter-card-momentum">
                <div className="analytics-recruiter-card-momentum-copy">
                  <span className="analytics-recruiter-card-momentum-label">{profile.sparklineLabel}</span>
                  <strong className={`analytics-recruiter-card-momentum-value ${deltaTone}`}>
                    {profile.sparklineValue}
                  </strong>
                </div>
                {sparkPath ? (
                  <svg className={`analytics-recruiter-card-sparkline ${deltaTone}`} viewBox="0 0 72 26" aria-hidden="true">
                    <path d={sparkPath} fill="none" strokeWidth="2.8" strokeLinecap="round" />
                  </svg>
                ) : null}
              </div>

              <div className="analytics-recruiter-card-footer">
                <div className="analytics-recruiter-card-footnote">
                  {footerStats.map((stat) => `${stat.label} ${formatMetricValue(stat.value, stat.unit)}`).join(' • ')}
                </div>
                <div className="analytics-recruiter-card-cta">
                  <span>{profile.clickHint}</span>
                </div>
              </div>
            </article>
          );
        })}
      </div>
    </div>
  );
};

export default RecruiterCardsVisual;
